import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';

const AlwaysVisibleInput = (props) => <components.Input {...props} isHidden={false} />;
const GenericAsyncSelect = forwardRef((props, ref) => {
    const {
        loadOptions,
        onItemClick,
        onClose,
        placeholder = "Selecione uma opção...",
        value = null,
        dataTest = "async-select",
        customOptionComponent = null,
        style = {},
        maintainInputValue = false,
        onInputValueChange = null,
        cacheUniqs = [],
    } = props;

    const [selectedValue, setSelectedValue] = useState(value);
    const [inputValue, setInputValue] = useState(() => value?.label ?? '');
    const selectRef = useRef(null);
    const justSelected = useRef(false);

    useImperativeHandle(ref, () => ({
        blur: () => {
            selectRef.current?.blur?.();
        },
        focus: () => {
            selectRef.current?.focus?.();
        },
    }));

    useEffect(() => {
        if (!maintainInputValue) {
            const nextValue = value?.value ?? value;
            const currentValue = selectedValue?.value ?? selectedValue;
            if (nextValue !== currentValue || value?.label !== selectedValue?.label) {
                setSelectedValue(value);
            }
            if (value?.label) {
                setInputValue(value.label);
            } else if (!value) {
                setInputValue('');
            }
            return;
        }
        if (value?.label) {
            setInputValue(value.label);
        }
    // Depende do conteúdo (value/label), não da referência do objeto — evita reset
    // do inputValue quando o pai re-renderiza criando um novo objeto com os mesmos dados.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value?.value, value?.label, maintainInputValue]);

    const handleChange = (selectedOption) => {
        if (!maintainInputValue) {
            setSelectedValue(selectedOption);
        } else {
            setSelectedValue(null);
        }

        if (selectedOption && onItemClick) {
            onItemClick(selectedOption.value);
        }

        if (selectedOption && maintainInputValue) {
            const label = selectedOption.label || String(selectedOption.value ?? '');
            setInputValue(label);
            justSelected.current = true;
        } else if (!maintainInputValue) {
            setInputValue('');
        } else if (!selectedOption) {
            setInputValue('');
            if (onInputValueChange) {
                onInputValueChange('');
            }
        }
    };

    const handleInputChange = (newInputValue, {action}) => {
        if (action === 'input-change') {
            if (justSelected.current) {
                justSelected.current = false;
                let typed;
                if (newInputValue.length > inputValue.length) {
                    // Acha o prefixo comum e extrai só os caracteres inseridos
                    let i = 0;
                    while (i < inputValue.length && inputValue[i] === newInputValue[i]) i++;
                    typed = newInputValue.slice(i, i + (newInputValue.length - inputValue.length));
                } else {
                    // Substituição ou paste — usa o novo valor direto
                    typed = newInputValue;
                }
                setInputValue(typed);
                if (onInputValueChange) onInputValueChange(typed);
                return;
            }
            setInputValue(newInputValue);
            if (onInputValueChange) {
                onInputValueChange(newInputValue);
            }
        } else if (action === 'set-value') {
            if (!maintainInputValue) {
                setInputValue('');
            }
        } else if (action === 'menu-close' && !maintainInputValue) {
            setInputValue('');
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Backspace' && justSelected.current) {
            justSelected.current = false;
            setInputValue('');
            event.preventDefault();
            return;
        }
        if (event.key === 'Escape') {
            setInputValue('');
            setSelectedValue(null);
            selectRef.current?.blur?.();
        }
    };

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '33px',
            height: '33px',
            width: style.width || '100%',
            maxWidth: '100%',
            border: state.isFocused ? '1px solid #2980b9' : '1px solid #3498db',
            borderRadius: '5px',
            boxShadow: 'none',
            '&:hover': {
                border: '1px solid #2980b9'
            },
            alignItems: 'center'
        }),
        option: (provided, state) => ({
            ...provided,
            padding: style.optionPadding || '10px 12px',
            minHeight: style.optionMinHeight || '40px',
            display: 'flex',
            alignItems: 'center',
            fontSize: style.optionFontSize || '15px',
            lineHeight: 1.35,
            backgroundColor: state.isFocused ? '#e8f4fc' : 'transparent',
            cursor: 'pointer',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            width: style.menuWidth || style.width || provided.width,
            minWidth: style.menuMinWidth || style.menuWidth || style.width || provided.width,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 10050
        }),
        input: (provided) => ({
            ...provided,
            padding: 0,
            margin: 0,
            opacity: 1,
            color: 'var(--color-text-primary)',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#333'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c757d'
        }),
        indicatorsContainer: () => ({
            display: 'none'
        }),
        dropdownIndicator: () => ({
            display: 'none'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    return (
        <div data-test={dataTest} style={{width: style.width || '100%', maxWidth: '100%'}}>
            <AsyncSelect
                ref={selectRef}
                cacheOptions
                cacheUniqs={cacheUniqs}
                loadOptions={loadOptions}
                onChange={handleChange}
                onInputChange={handleInputChange}
                onMenuClose={() => {
                    if (onClose) {
                        onClose();
                    }
                }}
                onBlur={() => { justSelected.current = inputValue.length > 0; }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                value={maintainInputValue ? null : (selectedValue || value)}
                inputValue={inputValue}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customStyles}
                noOptionsMessage={() => null}
                loadingMessage={() => "Carregando..."}
                isClearable
                isSearchable
                blurInputOnSelect={false}
                controlShouldRenderValue={false}
                defaultMenuIsOpen={false}
                components={{
                    Input: AlwaysVisibleInput,
                    ...(customOptionComponent ? { Option: customOptionComponent } : {}),
                }}
            />
        </div>
    );
});

GenericAsyncSelect.displayName = 'GenericAsyncSelect';

export default GenericAsyncSelect;
